import React, { useState, useEffect } from 'react';
import { adminClient } from '@/api/adminClient';
import type { OrganizationDetails, UsageStats, Subscription, Plan } from '@/types';

export default function OrganizationPage() {
  const [organization, setOrganization] = useState<OrganizationDetails | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [orgData, usageData, subData] = await Promise.all([
        adminClient.getOrganization(),
        adminClient.getUsageStats(),
        adminClient.getSubscription(),
      ]);

      setOrganization(orgData);
      setUsage(usageData);
      setSubscription(subData);
      setEditName(orgData.name);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editName.trim()) {
      setError('Organization name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const updated = await adminClient.updateOrganization({ name: editName.trim() });
      setOrganization(updated);
      setIsEditing(false);
      setSuccess('Organization updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const getPlanBadgeStyle = (plan: Plan) => {
    const baseStyle: React.CSSProperties = {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
    };

    switch (plan) {
      case 'enterprise':
        return { ...baseStyle, backgroundColor: '#805ad5', color: 'white' };
      case 'pro':
        return { ...baseStyle, backgroundColor: '#3182ce', color: 'white' };
      default:
        return { ...baseStyle, backgroundColor: '#718096', color: 'white' };
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    const baseStyle: React.CSSProperties = {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
    };

    switch (status) {
      case 'active':
        return { ...baseStyle, backgroundColor: '#c6f6d5', color: '#276749' };
      case 'trialing':
        return { ...baseStyle, backgroundColor: '#bee3f8', color: '#2b6cb0' };
      case 'past_due':
        return { ...baseStyle, backgroundColor: '#fed7d7', color: '#c53030' };
      case 'canceled':
        return { ...baseStyle, backgroundColor: '#e2e8f0', color: '#4a5568' };
      default:
        return { ...baseStyle, backgroundColor: '#e2e8f0', color: '#4a5568' };
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return '#e53e3e';
    if (percentage >= 70) return '#dd6b20';
    return '#38a169';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading organization data...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Organization Settings</h1>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={styles.errorBanner}>
          <span>Error: {error}</span>
          <button onClick={() => setError(null)} style={styles.dismissButton}>×</button>
        </div>
      )}
      {success && (
        <div style={styles.successBanner}>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} style={styles.dismissButton}>×</button>
        </div>
      )}

      <div style={styles.grid}>
        {/* Organization Profile Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Organization Profile</h2>
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} style={styles.editButton}>
                Edit
              </button>
            )}
          </div>

          <div style={styles.cardContent}>
            {isEditing ? (
              <div style={styles.editForm}>
                <label style={styles.label}>Organization Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  style={styles.input}
                  placeholder="Organization name"
                />
                <div style={styles.editActions}>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditName(organization?.name || '');
                    }}
                    style={styles.cancelButton}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    style={styles.saveButton}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={styles.profileRow}>
                  <div style={styles.avatar}>
                    {organization?.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={styles.orgName}>{organization?.name}</div>
                    <div style={styles.orgSlug}>@{organization?.slug}</div>
                  </div>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Organization ID</span>
                  <span style={styles.infoValue}>{organization?.id}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Created</span>
                  <span style={styles.infoValue}>{formatDate(organization?.createdAt)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Current Plan Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Current Plan</h2>
          </div>
          <div style={styles.cardContent}>
            <div style={styles.planSection}>
              <div style={styles.planHeader}>
                <span style={getPlanBadgeStyle(organization?.plan || 'free')}>
                  {organization?.plan || 'Free'}
                </span>
                <span style={getStatusBadgeStyle(subscription?.status || 'none')}>
                  {subscription?.status || 'None'}
                </span>
              </div>

              <div style={styles.planLimits}>
                <div style={styles.limitItem}>
                  <span style={styles.limitIcon}>🚪</span>
                  <span>Max {organization?.maxRooms || 2} Rooms</span>
                </div>
                <div style={styles.limitItem}>
                  <span style={styles.limitIcon}>👥</span>
                  <span>Max {organization?.maxUsers || 3} Users</span>
                </div>
                <div style={styles.limitItem}>
                  <span style={styles.limitIcon}>📡</span>
                  <span>Max {organization?.maxConcurrentListeners || 50} Listeners</span>
                </div>
                <div style={styles.limitItem}>
                  <span style={styles.limitIcon}>⏱️</span>
                  <span>Max {organization?.maxMonthlyMinutes || 60} min/month</span>
                </div>
              </div>

              {subscription?.currentPeriodEnd && (
                <div style={styles.periodInfo}>
                  <span style={styles.infoLabel}>Current Period Ends</span>
                  <span style={styles.infoValue}>{formatDate(subscription.currentPeriodEnd)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Usage Statistics Card */}
        <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Usage This Period</h2>
            {usage?.period && (
              <span style={styles.periodBadge}>
                {formatDate(usage.period.start)} - {formatDate(usage.period.end)}
              </span>
            )}
          </div>
          <div style={styles.cardContent}>
            <div style={styles.usageGrid}>
              {/* Minutes Usage */}
              <div style={styles.usageItem}>
                <div style={styles.usageHeader}>
                  <span style={styles.usageLabel}>Translation Minutes</span>
                  <span style={styles.usageValue}>
                    {usage?.minutes?.used ?? 0} / {usage?.minutes?.limit ?? 0}
                  </span>
                </div>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${Math.min(usage?.minutes?.percentage ?? 0, 100)}%`,
                      backgroundColor: getProgressColor(usage?.minutes?.percentage ?? 0),
                    }}
                  />
                </div>
                <span style={styles.usagePercent}>{usage?.minutes?.percentage ?? 0}% used</span>
              </div>

              {/* Rooms Usage */}
              <div style={styles.usageItem}>
                <div style={styles.usageHeader}>
                  <span style={styles.usageLabel}>Active Rooms</span>
                  <span style={styles.usageValue}>
                    {usage?.rooms?.used ?? 0} / {usage?.rooms?.limit ?? 0}
                  </span>
                </div>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${Math.min(usage?.rooms?.percentage ?? 0, 100)}%`,
                      backgroundColor: getProgressColor(usage?.rooms?.percentage ?? 0),
                    }}
                  />
                </div>
                <span style={styles.usagePercent}>{usage?.rooms?.percentage ?? 0}% used</span>
              </div>

              {/* Users Usage */}
              <div style={styles.usageItem}>
                <div style={styles.usageHeader}>
                  <span style={styles.usageLabel}>Team Members</span>
                  <span style={styles.usageValue}>
                    {usage?.users?.used ?? 0} / {usage?.users?.limit ?? 0}
                  </span>
                </div>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${Math.min(usage?.users?.percentage ?? 0, 100)}%`,
                      backgroundColor: getProgressColor(usage?.users?.percentage ?? 0),
                    }}
                  />
                </div>
                <span style={styles.usagePercent}>{usage?.users?.percentage ?? 0}% used</span>
              </div>
            </div>
          </div>
        </div>

        {/* Language Configuration Card */}
        <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Language Configuration</h2>
          </div>
          <div style={styles.cardContent}>
            <div style={styles.languageSection}>
              <div style={styles.languageItem}>
                <span style={styles.languageLabel}>Default Source Language</span>
                <span style={styles.languageBadge}>
                  {organization?.defaultSourceLanguage || 'ko'}
                </span>
              </div>
              <div style={styles.languageItem}>
                <span style={styles.languageLabel}>Enabled Target Languages</span>
                <div style={styles.languageTags}>
                  {(organization?.enabledTargetLanguages || []).length > 0 ? (
                    organization?.enabledTargetLanguages.map((lang) => (
                      <span key={lang} style={styles.languageTag}>{lang}</span>
                    ))
                  ) : (
                    <span style={styles.noLanguages}>No languages configured</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Information Card */}
        <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>결제 안내</h2>
          </div>
          <div style={styles.cardContent}>
            <div style={styles.paymentSection}>
              {/* Bank Info */}
              <div style={styles.bankInfoBox}>
                <div style={styles.bankInfoHeader}>
                  <span style={styles.bankIcon}>🏦</span>
                  <span style={styles.bankTitle}>계좌이체 정보</span>
                </div>
                <p style={styles.bankDescription}>
                  번역 시간 충전이 필요하시면 아래 계좌로 입금 후 관리자에게 연락해 주세요.
                </p>
                <div style={styles.bankDetails}>
                  <div style={styles.bankRow}>
                    <span style={styles.bankLabel}>은행</span>
                    <span style={styles.bankValue}>기업은행</span>
                  </div>
                  <div style={styles.bankRow}>
                    <span style={styles.bankLabel}>계좌번호</span>
                    <span style={styles.bankAccountNumber}>111-178766-01-018</span>
                  </div>
                  <div style={styles.bankRow}>
                    <span style={styles.bankLabel}>예금주</span>
                    <span style={styles.bankValue}>(주)라피엘</span>
                  </div>
                </div>
                <div style={styles.contactInfo}>
                  <span>📧 문의: billing@kfera.com</span>
                </div>
              </div>

              {/* Pricing Table */}
              <div style={styles.pricingSection}>
                <h3 style={styles.pricingTitle}>📦 시간 패키지 요금 (유효기간 6개월)</h3>
                <table style={styles.pricingTable}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeader}>패키지</th>
                      <th style={styles.tableHeader}>시간당</th>
                      <th style={styles.tableHeader}>총 가격</th>
                      <th style={styles.tableHeader}>할인</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={styles.tableRow}>
                      <td style={styles.tableCell}>10시간</td>
                      <td style={styles.tableCell}>₩150,000</td>
                      <td style={styles.tableCellPrice}>₩1,500,000</td>
                      <td style={styles.tableCell}>-</td>
                    </tr>
                    <tr style={styles.tableRowAlt}>
                      <td style={styles.tableCell}>30시간</td>
                      <td style={styles.tableCell}>₩130,000</td>
                      <td style={styles.tableCellPrice}>₩3,900,000</td>
                      <td style={styles.tableCellDiscount}>13%</td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={styles.tableCell}>50시간</td>
                      <td style={styles.tableCell}>₩120,000</td>
                      <td style={styles.tableCellPrice}>₩6,000,000</td>
                      <td style={styles.tableCellDiscount}>20%</td>
                    </tr>
                    <tr style={styles.tableRowAlt}>
                      <td style={styles.tableCell}>100시간</td>
                      <td style={styles.tableCell}>₩110,000</td>
                      <td style={styles.tableCellPrice}>₩11,000,000</td>
                      <td style={styles.tableCellDiscount}>27%</td>
                    </tr>
                    <tr style={styles.tableRow}>
                      <td style={styles.tableCell}>200시간</td>
                      <td style={styles.tableCell}>₩90,000</td>
                      <td style={styles.tableCellPrice}>₩18,000,000</td>
                      <td style={styles.tableCellDiscount}>40%</td>
                    </tr>
                  </tbody>
                </table>

                <div style={styles.enterpriseBox}>
                  <div style={styles.enterpriseHeader}>
                    <span style={styles.enterpriseIcon}>🏢</span>
                    <span style={styles.enterpriseTitle}>Enterprise (무제한)</span>
                  </div>
                  <div style={styles.enterprisePrice}>
                    ₩36,000,000 <span style={styles.enterprisePeriod}>/월</span>
                  </div>
                  <p style={styles.enterpriseDescription}>
                    월간 무제한 사용, 우선 지원, 전용 계정 관리자 포함
                  </p>
                </div>

                <div style={styles.policyNote}>
                  <strong>이용 정책:</strong>
                  <ul style={styles.policyList}>
                    <li>분 단위로 실제 스트리밍 시간만큼 차감됩니다.</li>
                    <li>유효기간 내 미사용 시간은 소멸됩니다.</li>
                    <li>여러 패키지 구매 시 먼저 구매한 패키지부터 차감됩니다 (FIFO).</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: 0,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    color: '#718096',
    fontSize: '16px',
  },
  errorBanner: {
    backgroundColor: '#fed7d7',
    color: '#c53030',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successBanner: {
    backgroundColor: '#c6f6d5',
    color: '#276749',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dismissButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'inherit',
    padding: '0 4px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a202c',
    margin: 0,
  },
  cardContent: {
    padding: '24px',
  },
  editButton: {
    padding: '6px 16px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#4a5568',
  },
  input: {
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box',
  },
  editActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: '#e2e8f0',
    color: '#4a5568',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '8px 16px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  profileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  avatar: {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    backgroundColor: '#667eea',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '28px',
    fontWeight: 'bold',
  },
  orgName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a202c',
  },
  orgSlug: {
    fontSize: '14px',
    color: '#718096',
    marginTop: '4px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderTop: '1px solid #e2e8f0',
  },
  infoLabel: {
    fontSize: '14px',
    color: '#718096',
  },
  infoValue: {
    fontSize: '14px',
    color: '#1a202c',
    fontWeight: '500',
  },
  planSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  planHeader: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  planLimits: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  limitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#4a5568',
  },
  limitIcon: {
    fontSize: '16px',
  },
  periodInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  periodBadge: {
    fontSize: '12px',
    color: '#718096',
    backgroundColor: '#f7fafc',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  usageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px',
  },
  usageItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  usageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  usageLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#4a5568',
  },
  usageValue: {
    fontSize: '14px',
    color: '#1a202c',
    fontWeight: '600',
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  usagePercent: {
    fontSize: '12px',
    color: '#718096',
  },
  languageSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  languageItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  languageLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#4a5568',
  },
  languageBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    backgroundColor: '#667eea',
    color: 'white',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    width: 'fit-content',
  },
  languageTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  languageTag: {
    padding: '4px 10px',
    backgroundColor: '#e2e8f0',
    color: '#4a5568',
    borderRadius: '4px',
    fontSize: '13px',
  },
  noLanguages: {
    fontSize: '14px',
    color: '#a0aec0',
    fontStyle: 'italic',
  },
  // Payment Section Styles
  paymentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  bankInfoBox: {
    backgroundColor: '#f0fff4',
    border: '1px solid #9ae6b4',
    borderRadius: '12px',
    padding: '24px',
  },
  bankInfoHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  bankIcon: {
    fontSize: '24px',
  },
  bankTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#276749',
  },
  bankDescription: {
    fontSize: '14px',
    color: '#2f855a',
    marginBottom: '16px',
  },
  bankDetails: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  bankRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bankLabel: {
    fontSize: '14px',
    color: '#718096',
  },
  bankValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a202c',
  },
  bankAccountNumber: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#2b6cb0',
    fontFamily: 'monospace',
    letterSpacing: '1px',
  },
  contactInfo: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#4a5568',
  },
  pricingSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  pricingTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a202c',
    margin: 0,
  },
  pricingTable: {
    width: '100%',
    borderCollapse: 'collapse',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  },
  tableHeader: {
    backgroundColor: '#f7fafc',
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#4a5568',
    borderBottom: '1px solid #e2e8f0',
  },
  tableRow: {
    backgroundColor: 'white',
  },
  tableRowAlt: {
    backgroundColor: '#f7fafc',
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#4a5568',
    borderBottom: '1px solid #e2e8f0',
  },
  tableCellPrice: {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a202c',
    borderBottom: '1px solid #e2e8f0',
  },
  tableCellDiscount: {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#38a169',
    borderBottom: '1px solid #e2e8f0',
  },
  enterpriseBox: {
    backgroundColor: '#ebf8ff',
    border: '1px solid #90cdf4',
    borderRadius: '12px',
    padding: '24px',
  },
  enterpriseHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  enterpriseIcon: {
    fontSize: '24px',
  },
  enterpriseTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2b6cb0',
  },
  enterprisePrice: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '8px',
  },
  enterprisePeriod: {
    fontSize: '16px',
    fontWeight: '400',
    color: '#718096',
  },
  enterpriseDescription: {
    fontSize: '14px',
    color: '#4a5568',
    margin: 0,
  },
  policyNote: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '16px',
    fontSize: '14px',
    color: '#92400e',
  },
  policyList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
};
